import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route } from 'react-router';
import { Routes } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { client as publicClientApplication } from './utilities/AuthUtils';
import NavMenu from './components/NavMenu';
import Footer from './components/Footer';
import { useIdleTimer } from "react-idle-timer";
import { Spinner } from 'react-bootstrap';

import './custom.css';

import { AlertProvider } from './components/Alerts/AlertContext';
import NotificationAlert from './components/Alerts/NotificationAlert';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { signOut } from './utilities/AuthUtils';
import { useHttpUtils } from './utilities/useHttpUtils';

import QIRsList from './components/Pages/QIRsList';
import ViewQIR from './components/Pages/QIR/ViewQIR';
import UpsertQIR from './components/Pages/QIR/UpsertQIR';
import { usePrivileges } from './hooks/usePrivileges';
import Configuration from './components/Pages/Configuration';
import { BusinessUnitProvider } from './contexts/BusinessUnitAccessContext';
import { PrivilegesProvider } from './contexts/PrivilegesContext';
import { useUserAuthorization } from './utilities/useUserAuthorization';

function LegacyApp(props) {

    const { t } = props;
    const { fetchDataAuthenticated } = useHttpUtils();

    const defaultIdleTimeout = 30;
    const [idleTimeout, setIdleTimeout] = useState(defaultIdleTimeout);
    useIdleTimer({
        onIdle: () => signOut(),
        timeout: 1000 * 60 * idleTimeout
    });

    const account = publicClientApplication.getAllAccounts()[0];
    const { contributor, canConfigure } = usePrivileges();
    const { checkingAuthorization, userIsAuthorized } = useUserAuthorization();

    useEffect(() => {
        if (!checkingAuthorization && userIsAuthorized) {
            fetchDataAuthenticated('api/config/idletimeout')
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error();
                })
                .then(data => {
                    setIdleTimeout(data);
                })
                .catch(() => {
                    console.log(`Failed to get idle timeout from config. Setting default value '${defaultIdleTimeout}'`);
                });
        }
    }, [checkingAuthorization, userIsAuthorized, fetchDataAuthenticated]);

    if (checkingAuthorization) {
        return (
            <div className="d-flex flex-column align-items-center">
                <Spinner className="my-3" />
                <h5>{t("Authorization.CheckingUserAuthorization")}</h5>
            </div>
        );
    }

    if (!userIsAuthorized) {
        return (
            <div className="d-flex justify-content-center mt-3">
                <h5>{t("Authorization.UnauthorizedMessage")}</h5>
            </div>
        );
    }

    return (
        <>
            <HelmetProvider>
                <AlertProvider>
                    <BusinessUnitProvider>
                        <PrivilegesProvider>
                            <NotificationAlert />
                            <NavMenu title={t("Application.Abbreviation")} />
                            <Helmet>
                                <title>{t("Application.TabTitle")}</title>
                            </Helmet>
                            <Routes>
                                <Route path='/' element={<Navigate to='/qirs/open' />} />
                                <Route path='/qirs' exact element={<QIRsList tableName="qirs-table" />} />
                                <Route path='/qirs/my' element={<QIRsList user={account.username} tableName="my-qirs-table" />} />
                                <Route path='/qirs/open' element={<QIRsList closed={false} tableName="open-qirs-table" />} />
                                <Route path='/qirs/closed' element={<QIRsList closed={true} tableName="closed-qirs-table" />} />
                                <Route path='/qir/view/:qirId' element={<ViewQIR />} />
                                {contributor &&
                                    <>
                                        <Route path='/qir/create' element={<UpsertQIR action="create" />} />
                                        <Route path='/qir/:qirId' element={<UpsertQIR action="update" />} />
                                    </>
                                }
                                {canConfigure &&
                                    <Route path='/config' element={<Configuration />} />
                                }
                            </Routes>
                            <Footer />
                        </PrivilegesProvider>
                    </BusinessUnitProvider>
                </AlertProvider>
            </HelmetProvider>
        </>
    );
}

const TranslatedApp = withTranslation()(LegacyApp);

export default function App() {
    return (
        <Suspense fallback="loading">
            <TranslatedApp />
        </Suspense>
    );
}